<template>
  <div class="jim-scheduling-container tariff-page">

    <!-- Header -->
    <div class="page-header-row">
      <div class="page-header-left"></div>

      <h1 class="page-heading">
        TARIFF EXPOSURE
      </h1>

      <div class="page-header-right"></div>
    </div>


    <div class="main-content">

      <!-- =====================================================
           LEFT SIDE CONTROLS
      ====================================================== -->
      <div class="left-side-section">

        <div class="control">
          <label>Start Date</label>

          <input
            v-model="startDate"
            type="date"
          />
        </div>


        <div class="control">
          <label>End Date</label>

          <input
            v-model="endDate"
            type="date"
          />
        </div>


        <div class="control">
          <label>View</label>

          <select
            v-model="viewMode"
            class="select-input"
          >
            <option value="vendor">
              Vendor Summary
            </option>

            <option value="type">
              Type Summary
            </option>

            <option value="detail">
              Detail
            </option>
          </select>
        </div>


        <div class="control">
          <label>TYPE</label>

          <select
            v-model="typeFilter"
            class="select-input"
            :disabled="!reportLoaded"
          >
            <option value="">
              ALL
            </option>

            <option
              v-for="type in availableTypes"
              :key="type"
              :value="type"
            >
              {{ type }}
            </option>
          </select>
        </div>


        <div class="control">
          <label>Location</label>

          <select
            v-model="locationFilter"
            class="select-input"
            :disabled="!reportLoaded"
          >
            <option value="">
              ALL
            </option>

            <option
              v-for="loc in availableLocations"
              :key="loc"
              :value="loc"
            >
              {{ loc }}
            </option>
          </select>
        </div>


        <div class="control">
          <label>Search</label>

          <input
            v-model="searchText"
            type="text"
            placeholder="Vendor or item..."
            :disabled="!reportLoaded"
          />
        </div>


        <button
          class="plan-it-btn"
          :disabled="loading || !canRun"
          @click="runReport"
        >
          {{ loading ? "Loading..." : "Run Report" }}
        </button>

        <button
            class="plan-it-btn"
            :disabled="!reportLoaded"
            @click="printTariffExposure"
            >
            Print
        </button>


        <button
          class="plan-it-btn"
          :disabled="!reportLoaded"
          @click="exportCSV"
        >
          Export CSV
        </button>


        <button
          class="plan-it-btn"
          :disabled="!reportLoaded"
          @click="clearFilters"
        >
          Clear Filters
        </button>


        <!-- Missing conversion warning -->
        <div
          v-if="reportLoaded && missingConversionItems.length"
          class="panel warn-box"
        >
          <div class="warn-title">
            Missing Weight Conversions
            ({{ missingConversionItems.length }})
          </div>

          <div class="warn-list">
            <span
              v-for="item in missingConversionItems"
              :key="item"
              class="warn-chip"
            >
              {{ item }}
            </span>
          </div>
        </div>

      </div>


      <!-- =====================================================
           RIGHT SIDE
      ====================================================== -->
      <div class="right-side-section">

        <!-- Error -->
        <div
          v-if="error"
          class="panel error-box"
        >
          {{ error }}
        </div>


        <!-- Summary bar -->
        <div
          v-if="reportLoaded"
          class="summary-bar"
        >

          <div class="summary-item">
            <div class="summary-label">
              Period
            </div>

            <div class="summary-value summary-date">
              {{ report.start_date }}
              →
              {{ report.end_date }}
            </div>
          </div>


          <div class="summary-item">
            <div class="summary-label">
              Purchase $
            </div>

            <div class="summary-value">
              {{ formatMoney(filteredTotalAmount) }}
            </div>
          </div>


          <div class="summary-item">
            <div class="summary-label">
              Pounds
            </div>

            <div class="summary-value">
              {{ formatNumber(filteredTotalLbs, 0) }}
            </div>
          </div>


          <div class="summary-item">
            <div class="summary-label">
              Detail Rows
            </div>

            <div class="summary-value">
              {{ formatNumber(filteredRows.length, 0) }}
            </div>
          </div>


          <div class="summary-item">
            <div class="summary-label">
              Missing Conv.
            </div>

            <div
              class="summary-value"
              :class="{ 'warning-value': missingConversionItems.length > 0 }"
            >
              {{ missingConversionItems.length }}
            </div>
          </div>

        </div>


        <!-- ===================================================
             REPORT CONTENT
        ==================================================== -->

        <div
          v-if="reportLoaded"
          class="sheet-scroll-container"
        >

          <div class="sheet-wrapper">

            <div class="panel report-panel">

              <!-- =============================================
                   VENDOR SUMMARY
              ============================================== -->
              <div v-if="viewMode === 'vendor'">

                <div class="table-title-row">
                  <div class="table-title">
                    Vendor Summary
                  </div>

                  <div class="table-subtitle">
                    {{ vendorSummary.length }} vendor(s)
                  </div>
                </div>


                <div class="table-scroll">

                  <table class="tariff-table vendor-table">

                    <thead>
                      <tr>
                        <th class="text-col vendor-col">
                          Vendor #
                        </th>

                        <th class="text-col">
                          TYPE
                        </th>

                        <th class="num-col">
                          Items
                        </th>

                        <th class="text-col">
                          Locations
                        </th>

                        <th class="num-col">
                          Receipts
                        </th>

                        <th class="num-col">
                          Pounds
                        </th>

                        <th class="num-col">
                          Purchase $
                        </th>

                        <th class="num-col">
                          Missing Conv.
                        </th>
                      </tr>
                    </thead>


                    <tbody>

                      <tr
                        v-for="row in vendorSummary"
                        :key="row.vend_no"
                        :class="{ 'missing-row': row.missing_conversion_count > 0 }"
                      >

                        <td class="text-col vendor-cell">
                          {{ row.vend_no || "NO VENDOR" }}
                        </td>

                        <td class="text-col">
                          {{ row.types }}
                        </td>

                        <td class="num-col">
                          {{ row.item_count }}
                        </td>

                        <td class="text-col">
                          {{ row.locations }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.receipt_count, 0) }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.total_lbs, 0) }}
                        </td>

                        <td class="num-col">
                          {{ formatMoney(row.total_amount) }}
                        </td>

                        <td class="num-col">
                          <span
                            v-if="row.missing_conversion_count"
                            class="missing-text"
                          >
                            {{ row.missing_conversion_count }}
                          </span>

                          <span v-else>
                            -
                          </span>
                        </td>

                      </tr>


                      <tr v-if="vendorSummary.length === 0">
                        <td
                          colspan="8"
                          class="empty-row"
                        >
                          No vendors match the current filters.
                        </td>
                      </tr>

                    </tbody>

                  </table>

                </div>

              </div>


              <!-- =============================================
                   TYPE SUMMARY
              ============================================== -->
              <div v-else-if="viewMode === 'type'">

                <div class="table-title-row">
                  <div class="table-title">
                    Material TYPE Summary
                  </div>
                </div>


                <div class="table-scroll">

                  <table class="tariff-table">

                    <thead>
                      <tr>

                        <th class="text-col">
                          TYPE
                        </th>

                        <th class="num-col">
                          Vendors
                        </th>

                        <th class="num-col">
                          Items
                        </th>

                        <th class="text-col">
                          Locations
                        </th>

                        <th class="num-col">
                          Receipts
                        </th>

                        <th class="num-col">
                          Pounds
                        </th>

                        <th class="num-col">
                          Purchase $
                        </th>

                        <th class="num-col">
                          Missing Conv.
                        </th>

                      </tr>
                    </thead>


                    <tbody>

                      <tr
                        v-for="row in typeSummary"
                        :key="row.type"
                        :class="{ 'missing-row': row.missing_conversion_count > 0 }"
                      >

                        <td class="text-col type-cell">
                          {{ row.type || "UNKNOWN" }}
                        </td>

                        <td class="num-col">
                          {{ row.vendor_count }}
                        </td>

                        <td class="num-col">
                          {{ row.item_count }}
                        </td>

                        <td class="text-col">
                          {{ row.locations }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.receipt_count, 0) }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.total_lbs, 0) }}
                        </td>

                        <td class="num-col">
                          {{ formatMoney(row.total_amount) }}
                        </td>

                        <td class="num-col">
                          <span
                            v-if="row.missing_conversion_count"
                            class="missing-text"
                          >
                            {{ row.missing_conversion_count }}
                          </span>

                          <span v-else>
                            -
                          </span>
                        </td>

                      </tr>

                    </tbody>

                  </table>

                </div>

              </div>


              <!-- =============================================
                   DETAIL
              ============================================== -->
              <div v-else>

                <div class="table-title-row">
                  <div class="table-title">
                    Vendor / Item / Location Detail
                  </div>

                  <div class="table-subtitle">
                    {{ filteredRows.length }} row(s)
                  </div>
                </div>


                <div class="table-scroll">

                  <table class="tariff-table detail-table">

                    <thead>
                      <tr>

                        <th class="text-col vendor-col">
                          Vendor #
                        </th>

                        <th class="text-col type-col">
                          TYPE
                        </th>

                        <th class="text-col item-col">
                          Item
                        </th>

                        <th class="text-col loc-col">
                          Loc
                        </th>

                        <th class="num-col">
                          Receipts
                        </th>

                        <th class="date-col">
                          First
                        </th>

                        <th class="date-col">
                          Last
                        </th>

                        <th class="num-col">
                          Qty
                        </th>

                        <th class="num-col">
                          Lbs / Unit
                        </th>

                        <th class="num-col">
                          Total Lbs
                        </th>

                        <th class="num-col">
                          Purchase $
                        </th>

                        <th class="num-col">
                          $ / Lb
                        </th>

                        <th class="status-col">
                          Conversion
                        </th>

                      </tr>
                    </thead>


                    <tbody>

                      <tr
                        v-for="row in filteredRows"
                        :key="`${row.vend_no}_${row.item_no}_${row.loc}`"
                        :class="{ 'missing-row': row.conversion_missing }"
                      >

                        <td class="text-col vendor-cell">
                          {{ row.vend_no || "" }}
                        </td>

                        <td class="text-col type-cell">
                          {{ row.type || "" }}
                        </td>

                        <td class="text-col item-cell">
                          {{ row.item_no }}
                        </td>

                        <td class="text-col">
                          {{ row.loc }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.receipt_count, 0) }}
                        </td>

                        <td class="date-col">
                          {{ fmtDate(row.first_receipt_date) }}
                        </td>

                        <td class="date-col">
                          {{ fmtDate(row.last_receipt_date) }}
                        </td>

                        <td class="num-col">
                          {{ formatNumber(row.total_quantity, 2) }}
                        </td>

                        <td class="num-col">

                          <span v-if="!row.conversion_missing">
                            {{ formatNumber(row.lbs_per_unit, 4) }}
                          </span>

                          <span
                            v-else
                            class="missing-text"
                          >
                            -
                          </span>

                        </td>

                        <td class="num-col">

                          <span v-if="row.total_lbs != null">
                            {{ formatNumber(row.total_lbs, 0) }}
                          </span>

                          <span
                            v-else
                            class="missing-text"
                          >
                            Missing
                          </span>

                        </td>

                        <td class="num-col">
                          {{ formatMoney(row.total_amount) }}
                        </td>

                        <td class="num-col">

                          <span v-if="row.cost_per_lb != null">
                            {{ formatMoney(row.cost_per_lb) }}
                          </span>

                          <span v-else>
                            -
                          </span>

                        </td>

                        <td class="status-col">

                          <span
                            v-if="row.conversion_missing"
                            class="status-chip missing-chip"
                          >
                            Missing
                          </span>

                          <span
                            v-else
                            class="status-chip good-chip"
                          >
                            OK
                          </span>

                        </td>

                      </tr>


                      <tr v-if="filteredRows.length === 0">
                        <td
                          colspan="13"
                          class="empty-row"
                        >
                          No detail rows match the current filters.
                        </td>
                      </tr>

                    </tbody>

                  </table>

                </div>

              </div>

            </div>

          </div>

        </div>


        <div
          v-else
          class="panel hint-panel"
        >
          Select a date range and click
          <b>Run Report</b>.
        </div>

      </div>

    </div>

  </div>
</template>


<script>
import axios from "axios";

export default {
  name: "TariffExposureView",

  data() {
    const today = new Date();

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    return {
      startDate: "2025-01-01",
      endDate: `${yyyy}-${mm}-${dd}`,

      viewMode: "vendor",

      typeFilter: "",
      locationFilter: "",
      searchText: "",

      loading: false,
      error: null,

      report: null
    };
  },


  computed: {

    canRun() {
      return (
        !!this.startDate &&
        !!this.endDate &&
        this.startDate <= this.endDate
      );
    },


    reportLoaded() {
      return !!this.report;
    },


    rawRows() {
      return Array.isArray(this.report?.rows)
        ? this.report.rows
        : [];
    },


    availableTypes() {
      return [
        ...new Set(
          this.rawRows
            .map(r => String(r.type || "").trim())
            .filter(Boolean)
        )
      ].sort();
    },


    availableLocations() {
      return [
        ...new Set(
          this.rawRows
            .map(r => String(r.loc || "").trim())
            .filter(Boolean)
        )
      ].sort();
    },


    filteredRows() {
      const type = String(this.typeFilter || "")
        .trim()
        .toUpperCase();

      const loc = String(this.locationFilter || "")
        .trim()
        .toUpperCase();

      const search = String(this.searchText || "")
        .trim()
        .toUpperCase();


      return this.rawRows.filter(row => {

        const rowType = String(row.type || "")
          .trim()
          .toUpperCase();

        const rowLoc = String(row.loc || "")
          .trim()
          .toUpperCase();

        if (type && rowType !== type) {
          return false;
        }

        if (loc && rowLoc !== loc) {
          return false;
        }


        if (search) {
          const haystack = [
            row.vend_no,
            row.item_no,
            row.type,
            row.loc
          ]
            .map(v => String(v || "").toUpperCase())
            .join(" ");

          if (!haystack.includes(search)) {
            return false;
          }
        }


        return true;
      });
    },


    filteredTotalAmount() {
      return this.filteredRows.reduce(
        (sum, row) =>
          sum + Number(row.total_amount || 0),
        0
      );
    },


    filteredTotalLbs() {
      return this.filteredRows.reduce(
        (sum, row) =>
          sum + Number(row.total_lbs || 0),
        0
      );
    },


    missingConversionItems() {
      return [
        ...new Set(
          this.filteredRows
            .filter(row => row.conversion_missing)
            .map(row => String(row.item_no || "").trim())
            .filter(Boolean)
        )
      ].sort();
    },


    vendorSummary() {
      const map = new Map();


      for (const row of this.filteredRows) {

        const vendNo =
          String(row.vend_no || "").trim() ||
          "NO VENDOR";


        if (!map.has(vendNo)) {
          map.set(vendNo, {
            vend_no: vendNo,

            types_set: new Set(),
            items_set: new Set(),
            locations_set: new Set(),
            missing_items_set: new Set(),

            receipt_count: 0,
            total_lbs: 0,
            total_amount: 0
          });
        }


        const current = map.get(vendNo);


        if (row.type) {
          current.types_set.add(
            String(row.type).trim()
          );
        }


        if (row.item_no) {
          current.items_set.add(
            String(row.item_no).trim()
          );
        }


        if (row.loc) {
          current.locations_set.add(
            String(row.loc).trim()
          );
        }


        if (
          row.conversion_missing &&
          row.item_no
        ) {
          current.missing_items_set.add(
            String(row.item_no).trim()
          );
        }


        current.receipt_count +=
          Number(row.receipt_count || 0);

        current.total_lbs +=
          Number(row.total_lbs || 0);

        current.total_amount +=
          Number(row.total_amount || 0);
      }


      return Array.from(map.values())
        .map(row => ({
          vend_no: row.vend_no,

          types: Array.from(row.types_set)
            .sort()
            .join(", "),

          item_count:
            row.items_set.size,

          locations:
            Array.from(row.locations_set)
              .sort()
              .join(", "),

          receipt_count:
            row.receipt_count,

          total_lbs:
            row.total_lbs,

          total_amount:
            row.total_amount,

          missing_conversion_count:
            row.missing_items_set.size
        }))
        .sort((a, b) => {

          // Biggest purchase dollars first.
          const amt =
            Number(b.total_amount || 0) -
            Number(a.total_amount || 0);

          if (Math.abs(amt) > 0.001) {
            return amt;
          }

          return String(a.vend_no)
            .localeCompare(String(b.vend_no));
        });
    },


    typeSummary() {
      const map = new Map();


      for (const row of this.filteredRows) {

        const type =
          String(row.type || "").trim() ||
          "UNKNOWN";


        if (!map.has(type)) {
          map.set(type, {
            type,

            vendors_set: new Set(),
            items_set: new Set(),
            locations_set: new Set(),
            missing_items_set: new Set(),

            receipt_count: 0,
            total_lbs: 0,
            total_amount: 0
          });
        }


        const current = map.get(type);


        if (row.vend_no) {
          current.vendors_set.add(
            String(row.vend_no).trim()
          );
        }


        if (row.item_no) {
          current.items_set.add(
            String(row.item_no).trim()
          );
        }


        if (row.loc) {
          current.locations_set.add(
            String(row.loc).trim()
          );
        }


        if (
          row.conversion_missing &&
          row.item_no
        ) {
          current.missing_items_set.add(
            String(row.item_no).trim()
          );
        }


        current.receipt_count +=
          Number(row.receipt_count || 0);

        current.total_lbs +=
          Number(row.total_lbs || 0);

        current.total_amount +=
          Number(row.total_amount || 0);
      }


      return Array.from(map.values())
        .map(row => ({
          type: row.type,

          vendor_count:
            row.vendors_set.size,

          item_count:
            row.items_set.size,

          locations:
            Array.from(row.locations_set)
              .sort()
              .join(", "),

          receipt_count:
            row.receipt_count,

          total_lbs:
            row.total_lbs,

          total_amount:
            row.total_amount,

          missing_conversion_count:
            row.missing_items_set.size
        }))
        .sort((a, b) =>
          String(a.type)
            .localeCompare(String(b.type))
        );
    }

  },


  methods: {

    async runReport() {
      if (this.loading || !this.canRun) {
        return;
      }


      this.loading = true;
      this.error = null;
      this.report = null;


      try {

        const response = await axios.get(
          "/getTariffExposure",
          {
            params: {
              start: this.startDate,
              end: this.endDate
            }
          }
        );


        this.report = response.data;


      } catch (err) {

        console.error(
          "runTariffExposure error:",
          err
        );


        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load tariff exposure data.";

      } finally {

        this.loading = false;
      }
    },


    clearFilters() {
      this.typeFilter = "";
      this.locationFilter = "";
      this.searchText = "";
    },

    printTariffExposure() {
        if (!this.reportLoaded) {
            this.error = "Run the report before printing.";
            return;
        }

        let rows = [];

        if (this.viewMode === "vendor") {
            rows = this.vendorSummary;
        } else if (this.viewMode === "type") {
            rows = this.typeSummary;
        } else {
            rows = this.filteredRows;
        }

        const payload = {
            meta: {
            generatedAt: new Date().toISOString(),

            startDate: this.startDate,
            endDate: this.endDate,

            viewMode: this.viewMode,

            typeFilter: this.typeFilter,
            locationFilter: this.locationFilter,
            searchText: this.searchText
            },

            summary: {
            totalAmount: this.filteredTotalAmount,
            totalLbs: this.filteredTotalLbs,
            rowCount: rows.length
            },

            rows,

            missingConversionItems: this.missingConversionItems
        };

        sessionStorage.setItem(
            "pablo_tariff_exposure_print_payload",
            JSON.stringify(payload)
        );

        const routeData = this.$router.resolve({
            path: "/print-tariff-exposure"
        });

        window.open(routeData.href, "_blank");
    },


    fmtDate(value) {
      if (!value) {
        return "";
      }

      const d = new Date(value);

      if (Number.isNaN(d.getTime())) {
        return String(value);
      }

      const mm =
        String(d.getMonth() + 1)
          .padStart(2, "0");

      const dd =
        String(d.getDate())
          .padStart(2, "0");

      const yyyy =
        d.getFullYear();

      return `${mm}/${dd}/${yyyy}`;
    },


    formatNumber(value, decimals = 0) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "-";
      }


      const n = Number(value);

      if (!Number.isFinite(n)) {
        return "-";
      }


      return n.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }
      );
    },


    formatMoney(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "-";
      }


      const n = Number(value);

      if (!Number.isFinite(n)) {
        return "-";
      }


      const abs =
        Math.abs(n).toLocaleString(
          "en-US",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }
        );


      return n < 0
        ? `($${abs})`
        : `$${abs}`;
    },


    csvEscape(value) {
      if (
        value === null ||
        value === undefined
      ) {
        return '""';
      }

      return `"${String(value)
        .replace(/"/g, '""')}"`;
    },


    exportCSV() {
      if (!this.reportLoaded) {
        return;
      }


      const csvRows = [];


      // ------------------------------------------
      // Vendor summary export
      // ------------------------------------------
      if (this.viewMode === "vendor") {

        csvRows.push([
          "Vendor #",
          "TYPE",
          "Item Count",
          "Locations",
          "Receipt Count",
          "Total Pounds",
          "Purchase Dollars",
          "Missing Conversion Items"
        ]);


        for (const row of this.vendorSummary) {

          csvRows.push([
            row.vend_no,
            row.types,
            row.item_count,
            row.locations,
            row.receipt_count,
            row.total_lbs,
            row.total_amount,
            row.missing_conversion_count
          ]);
        }
      }


      // ------------------------------------------
      // Type summary export
      // ------------------------------------------
      else if (this.viewMode === "type") {

        csvRows.push([
          "TYPE",
          "Vendor Count",
          "Item Count",
          "Locations",
          "Receipt Count",
          "Total Pounds",
          "Purchase Dollars",
          "Missing Conversion Items"
        ]);


        for (const row of this.typeSummary) {

          csvRows.push([
            row.type,
            row.vendor_count,
            row.item_count,
            row.locations,
            row.receipt_count,
            row.total_lbs,
            row.total_amount,
            row.missing_conversion_count
          ]);
        }
      }


      // ------------------------------------------
      // Detail export
      // ------------------------------------------
      else {

        csvRows.push([
          "Vendor #",
          "TYPE",
          "Item",
          "Location",
          "Receipt Count",
          "First Receipt",
          "Last Receipt",
          "Total Quantity",
          "Lbs Per Inventory Unit",
          "Total Pounds",
          "Purchase Dollars",
          "Cost Per Lb",
          "Conversion Missing",
          "Product Details Match"
        ]);


        for (const row of this.filteredRows) {

          csvRows.push([
            row.vend_no,
            row.type,
            row.item_no,
            row.loc,
            row.receipt_count,
            this.fmtDate(row.first_receipt_date),
            this.fmtDate(row.last_receipt_date),
            row.total_quantity,
            row.lbs_per_unit,
            row.total_lbs,
            row.total_amount,
            row.cost_per_lb,
            row.conversion_missing
              ? "YES"
              : "NO",
            row.product_details_match
              ? "YES"
              : "NO"
          ]);
        }
      }


      const csv =
        "\ufeff" +
        csvRows
          .map(row =>
            row
              .map(value =>
                this.csvEscape(value)
              )
              .join(",")
          )
          .join("\n");


      const blob = new Blob(
        [csv],
        {
          type:
            "text/csv;charset=utf-8;"
        }
      );


      const url =
        URL.createObjectURL(blob);


      const link =
        document.createElement("a");


      const viewLabel =
        this.viewMode === "vendor"
          ? "vendor"
          : this.viewMode === "type"
            ? "type"
            : "detail";


      link.href = url;

      link.setAttribute(
        "download",
        `tariff_exposure_${viewLabel}_${this.startDate}_${this.endDate}.csv`
      );


      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }

  }
};
</script>


<style scoped>

/* ============================================================
   PAGE
============================================================ */

.tariff-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}


.jim-scheduling-container {
  font-family: Garamond, serif;

  -webkit-font-smoothing:
    antialiased;

  -moz-osx-font-smoothing:
    grayscale;

  color: #cbbfbf;

  display: flex;

  flex-direction: column;

  align-items: stretch;

  background-color: #0b0101;

  min-height: 100vh;
}


/* ============================================================
   HEADER
============================================================ */

.page-header-row {
  display: grid;

  grid-template-columns:
    1fr auto 1fr;

  align-items: center;

  margin-bottom: 10px;
}


.page-heading {
  font-family:
    "Cormorant Garamond",
    Garamond,
    serif;

  font-size: 36px;

  color: #cbbfbf;

  margin: 0;

  text-align: center;
}


/* ============================================================
   MAIN LAYOUT
============================================================ */

.main-content {
  display: grid;

  grid-template-columns:
    200px 1fr;

  gap: 12px;

  width: 100%;

  padding: 0 14px;

  box-sizing: border-box;

  align-items: start;
}


/* ============================================================
   LEFT CONTROLS
============================================================ */

.left-side-section {
  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 10px;

  margin-top: 80px;
}


.control {
  display: flex;

  flex-direction: column;

  gap: 6px;

  text-align: left;

  width: 100%;
}


.control label {
  font-size: 13px;

  color: #cbbfbf;

  font-weight: 600;
}


.control input,
.select-input {
  padding: 8px 10px;

  border-radius: 8px;

  border: 1px solid #444;

  background-color: #111;

  color: #cbbfbf;

  font-family: inherit;

  width: 100%;

  box-sizing: border-box;
}


.control input:disabled,
.select-input:disabled {
  opacity: 0.55;
}


/* ============================================================
   BUTTONS
============================================================ */

.plan-it-btn {
  border-radius: 10px;

  padding: 8px 16px;

  width: 160px;

  background-color: #f3f4f6;

  border: 1px solid #cfd3d8;

  color: #333;

  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  font-family: inherit;

  height: 38px;
}


.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;

  border-color: #bfc5cc;
}


.plan-it-btn:disabled {
  background-color: #e5e7eb;

  color: #888;

  cursor: not-allowed;
}


/* ============================================================
   RIGHT SIDE
============================================================ */

.right-side-section {
  width: 100%;

  min-width: 0;
}


/* ============================================================
   PANELS
============================================================ */

.panel {
  background-color: #0b0101;

  border: 1px solid #888;

  border-radius: 10px;

  padding: 10px 12px;

  text-align: left;
}


.report-panel {
  background-color: #708090a4;

  border-radius: 10px;

  border: 1px solid #888;
}


.hint-panel {
  margin-top: 20px;

  background-color: #0b0101;
}


/* ============================================================
   SUMMARY BAR
============================================================ */

.summary-bar {
  display: flex;

  justify-content:
    space-between;

  gap: 12px;

  padding: 10px 12px;

  border-radius: 10px;

  border: 1px solid #888;

  margin: 0 0 12px;

  background-color: #708090a4;

  flex-wrap: wrap;

  width: 100%;

  box-sizing: border-box;
}


.summary-item {
  min-width: 120px;

  flex: 1 1 120px;

  display: flex;

  flex-direction: column;

  align-items: center;

  text-align: center;
}


.summary-label {
  font-size: 16px;

  opacity: 0.85;

  line-height: 1.1;
}


.summary-value {
  font-size: 18px;

  font-weight: bold;

  color: #fff;

  line-height: 1.1;

  margin-top: 3px;

  word-break: break-word;
}


.summary-date {
  font-size: 14px;
}


.warning-value {
  color: #ffe58a;
}


/* ============================================================
   SCROLL AREA
============================================================ */

.sheet-scroll-container {
  max-height: 80vh;

  overflow-y: auto;

  width: 100%;

  border-radius: 10px;

  background-color: #0b0101;

  padding-right: 10px;

  box-sizing: border-box;
}


.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}


.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;

  border-radius: 8px;
}


.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}


.table-scroll {
  overflow-x: auto;

  padding-bottom: 6px;
}


/* ============================================================
   TABLE TITLES
============================================================ */

.table-title-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 12px;

  margin-bottom: 8px;
}


.table-title {
  font-size: 18px;

  font-weight: bold;

  color: #fff;
}


.table-subtitle {
  font-size: 12px;

  color: #fff;

  opacity: 0.8;
}


/* ============================================================
   TABLE
============================================================ */

.tariff-table {
  width: 100%;

  border-collapse: collapse;

  min-width: 1000px;
}


.detail-table {
  min-width: 1550px;
}


.tariff-table th,
.tariff-table td {
  border: 1px solid #333;

  padding: 6px 8px;

  font-size: 13px;

  white-space: nowrap;
}


.tariff-table th {
  background-color: #0b0101;

  color: #cbbfbf;

  font-weight: bold;

  position: sticky;

  top: 0;

  z-index: 3;
}


.tariff-table td {
  color: #e5e7eb;
}


.tariff-table tbody tr:hover td {
  background-color: #5595b7c2;

  color: #fff;
}


.text-col {
  text-align: left;
}


.num-col {
  text-align: right;
}


.date-col {
  text-align: center;

  min-width: 95px;
}


.vendor-col {
  min-width: 95px;
}


.type-col {
  min-width: 65px;
}


.item-col {
  min-width: 170px;
}


.loc-col {
  min-width: 50px;
}


.status-col {
  text-align: center;

  min-width: 90px;
}


.vendor-cell {
  font-weight: bold;

  color: #fff;
}


.type-cell {
  font-weight: bold;

  color: #fff;
}


.item-cell {
  color: #fff;
}


/* ============================================================
   MISSING CONVERSIONS
============================================================ */

.missing-row td {
  background-color:
    rgba(110, 78, 16, 0.28);
}


.missing-text {
  color: #ffe58a;

  font-weight: bold;
}


.status-chip {
  display: inline-block;

  padding: 2px 7px;

  border-radius: 999px;

  font-size: 11px;

  font-weight: bold;
}


.good-chip {
  border: 1px solid #5e8b61;

  background:
    rgba(42, 93, 46, 0.35);

  color: #d8f5da;
}


.missing-chip {
  border: 1px solid #a88a42;

  background:
    rgba(104, 78, 18, 0.45);

  color: #ffe58a;
}


/* ============================================================
   ERROR / WARNING
============================================================ */

.error-box {
  border-color: #aa4444;

  background: #2a0b0b;

  color: #fca5a5;

  width: 100%;

  box-sizing: border-box;

  margin-bottom: 12px;
}


.warn-box {
  border-color: #888844;

  background: #1f1a0b;

  color: #fef3c7;

  width: 100%;

  box-sizing: border-box;

  margin-top: 8px;
}


.warn-title {
  font-weight: bold;

  margin-bottom: 6px;
}


.warn-list {
  display: flex;

  flex-wrap: wrap;

  gap: 6px;
}


.warn-chip {
  padding: 3px 8px;

  border-radius: 999px;

  border: 1px solid #665;

  background: #120f08;

  font-size: 12px;
}


.empty-row {
  padding: 14px !important;

  font-size: 14px !important;

  opacity: 0.8;

  text-align: center !important;
}


/* ============================================================
   SMALLER SCREENS
============================================================ */

@media (max-width: 1100px) {

  .main-content {
    grid-template-columns:
      180px 1fr;
  }


  .summary-item {
    flex-basis: 160px;
  }

}
</style>